import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from 'react-redux';
import { AppActionCreators, IApplicationState, INkrDonorReferralConversionRateResponse, ReportActionCreators } from '@nkr/store/index';
import { INkrDonorReferralConversionRate } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { IHeaderParams, GridApi, GridReadyEvent, CsvExportParams, ProcessCellForExportParams, ICellRendererParams, ValueGetterParams, ColDef, FirstDataRenderedEvent, RowClassParams } from 'ag-grid-community';
import { ReportWrapper } from './styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Button, FormControl } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';

interface IOwnProps {
    isCenterLogin: boolean
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getNkrDonorReferralConversionRate: (referralYear: number, mininumReferrals: number, conversionRateCutoff: number) => Promise<INkrDonorReferralConversionRateResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: INkrDonorReferralConversionRate[],
    centerId: number,
    group: number,
    referralYear: number,
    minimumReferrals: string,
    conversionRateCutoff: string,
    gridApi: GridApi,
    columns: ColDef[]
}

class NkrDonorReferralConversionRate extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        centerId: 0,
        group: 0,
        referralYear: moment().year(),
        minimumReferrals: '20',
        conversionRateCutoff: '0',
        gridApi: null,
        columns: [
            {
                colId: 'rank',
                headerName: 'Rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Rank</SortableHeaderComponent>,
                width: 50,
                valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
            },

            {
                colId: 'centerAlias',
                field: 'centerAlias',
                headerName: 'Center',
                comparator: Utility.stringSortComparator,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
                width: 155,
                pinnedRowCellRendererFramework: (props: ICellRendererParams) => {
                    return <div className='text-right'>Summary &gt;&gt;</div>;
                },
                colSpan: (params: any) => {
                    if (params.data.centerId == 0) {
                        return 1;
                    }

                    return 1;
                }
            },

            {
                colId: 'referralCount',
                field: 'referralCount',
                headerName: 'Referral Count',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Referral Count</SortableHeaderComponent>,
                width: 155
            },

            {
                colId: 'donatedCount',
                field: 'donatedCount',
                headerName: 'Donated Count',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donated Count</SortableHeaderComponent>,
                width: 205
            },

            {
                colId: 'conversionRate',
                field: 'conversionRate',
                headerName: 'Conversion Rate',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Conversion Rate %</SortableHeaderComponent>,
                width: 155,
                cellRendererFramework: (props: ICellRendererParams) => {
                    let data = props.data as INkrDonorReferralConversionRate;

                    if (data.category == 'LV') {
                        return <div>LV</div>;
                    }

                    if (data.category == 'NV') {
                        return <div>NV</div>;
                    }

                    return <div>{Math.round(props.value)}</div>;
                }
            },

            {
                colId: 'networkRank',
                field: 'rank',
                headerName: 'Network Rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Network Rank</SortableHeaderComponent>,
                width: 155,
                pinnedRowCellRendererFramework: (props: ICellRendererParams) => {
                    return <div></div>;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    let data = props.data as INkrDonorReferralConversionRate;

                    if (data.category == 'LV') {
                        return <div>LV</div>;
                    }

                    if (data.category == 'NV') {
                        return <div>NV</div>;
                    }

                    return <div>{props.value}</div>;
                }
            }
        ]
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let { referralYear, minimumReferrals, conversionRateCutoff } = this.state;

        let minimumReferralsParm = parseInt(minimumReferrals);
        let conversionRateCutoffParm = parseFloat(conversionRateCutoff);

        this.props.showLoading('Getting nkr donor referral conversion rate, one moment please...').then(() => {
            this.props.getNkrDonorReferralConversionRate(referralYear, minimumReferralsParm, conversionRateCutoffParm).then(response => {
                this.setState({
                    data: response.data.results
                }, () => {
                    this.props.hideLoading();
                });
            });
        });
    }

    getTotals = (data: INkrDonorReferralConversionRate[]): INkrDonorReferralConversionRate => {
        let totalDonatedCount = _.sumBy(data, item => item.donatedCount);
        let totalReferralCount = _.sumBy(data, item => item.referralCount);
        let totalConversionRate = totalReferralCount > 0 ? (totalDonatedCount / totalReferralCount) * 100 : 0;

        return {
            centerId: 0,
            centerAlias: '',
            donatedCount: totalDonatedCount,
            referralCount: totalReferralCount,
            conversionRate: totalConversionRate,
            category: ''
        };
    }

    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerId: parseInt(e.currentTarget.value)
        });
    }

    handleGroupChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            group: parseInt(e.currentTarget.value)
        });
    }

    handleYearChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            referralYear: parseInt(e.currentTarget.value)
        });
    }

    handleMinimumReferralsChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            minimumReferrals: e.currentTarget.value
        });
    }

    handleConversionRateCutoffChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            conversionRateCutoff: e.currentTarget.value
        });
    }

    handleSubmit = () => {
        this.getData();
    }

    getFilteredAndSortedData = () => {
        let filteredData = this.state.data.filter(x => {
            let center = this.state.centerId > 0 ? x.centerId == this.state.centerId : true;
            let group = true;

            return center && group;
        });

        let sortedData = _.orderBy(filteredData, [
            item => item.conversionRate
        ], ['asc', 'asc']);

        return sortedData;
    }

    getCenterList = () => {
        let centerListRaw = this.state.data.map(item => {
            return { id: item.centerId, alias: item.centerAlias };
        });

        let centerList = _.sortBy(_.uniqBy(centerListRaw, item => item.id), item => item.alias);

        return centerList;
    }

    getEnableSubmit = () => {
        let { minimumReferrals, conversionRateCutoff } = this.state;
        let enableSubmit = Utility.isNumber(minimumReferrals) && Utility.isNumber(conversionRateCutoff);

        return enableSubmit;
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: `nkr-donor-referral-conversion-rate.csv`,
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.colId == 'networkRank' || colDef.colId == 'conversionRate') {

                    if (params.node.data.category == 'LV') {
                        return 'LV';
                    }
                    if (params.node.data.category == 'NV') {
                        return 'NV';
                    }
                }
                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        const sortedData = this.getFilteredAndSortedData();
        const centerList = this.getCenterList();
        const totals = this.getTotals(this.state.data);
        const enableSubmit = this.getEnableSubmit();
        const referralYears = _.range(moment().year(), 2015, -1);

        return (
            <>
                {this.props.isCenterLogin == false && (
                    <>
                        <div className='well well-sm' style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 }}>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: 5 }}>Year:</div>
                                <div style={{ marginRight: 20 }}>
                                    <FormControl
                                        componentClass='select'
                                        style={{ width: 85 }}
                                        value={this.state.referralYear}
                                        onChange={this.handleYearChange}
                                    >
                                        {referralYears.map(year =>
                                            <option value={year}>{year}</option>
                                        )}
                                    </FormControl>
                                </div>

                                <div style={{ marginRight: 5 }}>Minimum Referrals:</div>
                                <div style={{ marginRight: 20 }}>
                                    <FormControl
                                        style={{ width: 65 }}
                                        value={this.state.minimumReferrals}
                                        onChange={this.handleMinimumReferralsChange}
                                    />
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                    <div style={{ marginRight: 5 }}>Conversion Rate Cutoff (%):</div>
                                    <div>
                                        <FormControl
                                            style={{ width: 65 }}
                                            value={this.state.conversionRateCutoff}
                                            onChange={this.handleConversionRateCutoffChange}
                                        />
                                    </div>
                                </div>

                                <Button bsStyle='primary' disabled={!enableSubmit} onClick={this.handleSubmit}>Update</Button>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 5 }}>Center:</div>
                                    <div style={{ marginRight: 20 }}>
                                        <FormControl
                                            componentClass='select'
                                            value={this.state.centerId}
                                            onChange={this.handleCenterChange}>
                                            <option value={0}>- All Centers -</option>
                                            {centerList.map(center =>
                                                <option key={`ctr-${center.id}`} value={center.id}>{center.alias}</option>
                                            )}
                                        </FormControl>
                                    </div>
                                </div>

                                <div>
                                    <Button onClick={this.handleExport}>Export to CSV</Button>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <ReportWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true,
                            resizable: false
                        }}
                        columnDefs={this.state.columns}
                        rowData={sortedData}
                        headerHeight={30}
                        groupHeaderHeight={30}
                        rowHeight={30}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        noRowsOverlayComponentFramework={() => <div>No center information available...</div>}
                        pinnedBottomRowData={[totals]}
                        onGridReady={this.onGridReady}
                        onFirstDataRendered={this.onFirstDataRendered}
                        getRowStyle={(params: RowClassParams) => {
                            let data = params.data as INkrDonorReferralConversionRate;

                            if (data.centerId == 0) {
                                return { background: '#133366;', color: 'white' };
                            }

                            if (data.category == 'Green') {
                                return { background: 'lightgreen' };
                            }

                            if (data.category == 'LV') {
                                return { background: 'lightyellow' };
                            }

                            if (data.category == 'NV') {
                                return { background: 'lightblue' };
                            }

                            if (data.category == 'Red') {
                                return { background: '#ff8080' };
                            }
                        }}
                    />
                </ReportWrapper>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getNkrDonorReferralConversionRate: (referralYear, mininumReferrals, conversionRateCutoff) => {
            return dispatch(ReportActionCreators.getNkrDonorReferralConversionRate(referralYear, mininumReferrals, conversionRateCutoff));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(NkrDonorReferralConversionRate);